import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Check, Crown, Zap, Star, ArrowLeft, CreditCard, CheckCircle, User, MessageCircle, Mail, FileText, RefreshCw, Loader2, ArrowUpDown, AlertTriangle, XCircle, Clock } from "lucide-react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";
import { validateCpfCnpj, validatePhone, validateForm, cleanPhone } from "@/utils/validators";
import { errorHandler } from "@/utils/errorHandler";
import { logger } from "@/utils/logger";
import { performanceMonitor } from "@/utils/performanceMonitor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { asaasService } from "@/integrations/asaas/service";
import { useInternationalization, useTranslations } from "@/contexts/InternationalizationContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useTrialProtection } from "@/hooks/useTrialProtection";
import { SubscriptionExpiredScreen } from "@/components/SubscriptionExpiredScreen";
import { SubscriptionActiveScreen } from "@/components/SubscriptionActiveScreen";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { UpgradeToQuarterlyModal } from "@/components/UpgradeToQuarterlyModal";

// Hook personalizado para timer de contagem regressiva
const useCountdownTimer = (targetDate: Date) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  originalPrice?: number;
  discount?: string;
  promoLabel?: string;
  monthlyEquivalent?: number;
  featured?: boolean;
  features: string[];
  popular?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const plans: Plan[] = [
  {
    id: "monthly-basic",
    name: "Básico Mensal",
    price: 39.00,
    period: "mês",
    features: [
      "Gestão completa de pedidos",
      "Calculadora de preços profissional",
      "Catálogo de produtos",
      "Relatórios financeiros",
      "Integração WhatsApp",
      "Suporte por email",
      "Backup automático",
      "1 operador (apenas proprietário)",
      "Sem emissão de notas fiscais"
    ],
    icon: Zap,
    color: "blue"
  },
  // 🆕 Plano Trimestral movido para ficar ao lado do Básico Mensal
  // 🎁 PROMOÇÃO DE LANÇAMENTO: R$ 79,90 (de R$ 99,90)
  {
    id: "quarterly-basic",
    name: "Básico Trimestral",
    price: 79.90, // 🎁 Preço promocional
    period: "trimestre",
    originalPrice: 99.90, // Preço regular do trimestral
    discount: "🎁 Pague 2 meses e use por 3",
    promoLabel: "🎁 Promoção de Lançamento",
    monthlyEquivalent: 26.63, // R$ 79,90 ÷ 3 meses
    featured: true, // Destaque visual
    features: [
      "Gestão completa de pedidos",
      "Calculadora de preços profissional",
      "Catálogo de produtos",
      "Relatórios financeiros",
      "Integração WhatsApp",
      "Suporte por email",
      "Backup automático",
      "1 operador (apenas proprietário)",
      "Sem emissão de notas fiscais"
    ],
    icon: Zap,
    color: "blue"
  },
  {
    id: "yearly-basic",
    name: "Básico Anual",
    price: 390.00,
    period: "ano",
    originalPrice: 468.00,
    discount: "2 meses grátis",
    features: [
      "Tudo do Básico Mensal",
      "2 meses grátis",
      "Suporte prioritário",
      "Recursos premium",
      "Integração avançada",
      "Relatórios detalhados",
      "Backup premium",
      "1 operador (apenas proprietário)",
      "Sem emissão de notas fiscais"
    ],
    icon: Crown,
    color: "purple"
  },
  {
    id: "monthly-professional",
    name: "Profissional Mensal",
    price: 99.90,
    period: "mês",
    features: [
      "Tudo do plano Básico",
      "Emissão de Notas Fiscais (NFe e NFCe)",
      "Até 3 operadores colaboradores",
      "1 CNPJ",
      "Até 100 notas fiscais por mês",
      "Integração Focus NFe",
      "Alertas de uso de notas",
      "Suporte prioritário",
      "Backup premium"
    ],
    popular: true,
    icon: Star,
    color: "gold"
  },
  // 🆕 Profissional Trimestral movido para ficar ao lado do Profissional Mensal
  {
    id: "quarterly-professional",
    name: "Profissional Trimestral",
    price: 280.00,
    period: "trimestre",
    originalPrice: 299.70,
    discount: "💼 Upgrade com desconto",
    monthlyEquivalent: 93.33, // R$ 280,00 ÷ 3 meses
    features: [
      "Tudo do plano Básico",
      "Emissão de Notas Fiscais (NFe e NFCe)",
      "Até 3 operadores colaboradores",
      "1 CNPJ",
      "Até 100 notas fiscais por mês",
      "Integração Focus NFe",
      "Alertas de uso de notas",
      "Suporte prioritário",
      "Backup premium"
    ],
    icon: Star,
    color: "gold"
  },
  {
    id: "monthly-professional-plus",
    name: "Profissional Plus Mensal",
    price: 189.90,
    period: "mês",
    features: [
      "Tudo do Profissional Mensal",
      "Até 5 operadores colaboradores",
      "1 CNPJ",
      "Até 300 notas/mês",
      "Alertas de uso + recomendação de upgrade",
      "Prioridade maior no suporte"
    ],
    icon: Crown,
    color: "purple"
  },
  {
    id: "monthly-professional-enterprise",
    name: "Profissional Enterprise Mensal",
    price: 389.90,
    period: "mês",
    features: [
      "Tudo do Profissional Plus",
      "Até 5 operadores colaboradores",
      "1 CNPJ",
      "Até 1.000 notas/mês",
      "Suporte ainda mais próximo",
      "Preparado para alto volume",
      "Foco em crescimento e estabilidade"
    ],
    icon: Crown,
    color: "purple"
  },
  {
    id: "yearly-professional",
    name: "Profissional Anual",
    price: 999.90,
    period: "ano",
    originalPrice: 1199.80,
    discount: "2 meses grátis",
    features: [
      "Emissão de Notas Fiscais (NFe e NFCe)",
      "Até 3 operadores colaboradores",
      "1 CNPJ",
      "Até 100 notas fiscais por mês",
      "2 meses grátis",
      "Integração Focus NFe",
      "Suporte prioritário",
      "Backup premium"
    ],
    icon: Crown,
    color: "purple"
  },
  
];

export default function Assinatura() {
  // HOOKS E VARIÁVEIS NO INÍCIO
  const { user, empresa, refreshEmpresa } = useAuth();
  const navigate = useNavigate();
  const { formatCurrency, getPricing } = useInternationalization();
  const t = useTranslations();
  const { isTrialExpired, trialEndDate, daysRemaining } = useTrialProtection();

  // Timer para garantia de preço (31/12/2025)
  const priceGuaranteeEnd = new Date('2025-12-31T23:59:59');
  const priceTimer = useCountdownTimer(priceGuaranteeEnd);
  const [selectedPlan, setSelectedPlan] = useState<string>("yearly");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("PIX_AUTOMATIC");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalDismissed, setUpgradeModalDismissed] = useState(false);
  const [showPixInstructions, setShowPixInstructions] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [paymentEmail, setPaymentEmail] = useState<string>("");
  const [isRenewal, setIsRenewal] = useState(false);
  const [dialogTrocarPlanoOpen, setDialogTrocarPlanoOpen] = useState(false);
  const [dialogTrocarPagamentoOpen, setDialogTrocarPagamentoOpen] = useState(false);
  const [trocandoPlano, setTrocandoPlano] = useState(false);
  const [trocandoPagamento, setTrocandoPagamento] = useState(false);
  const [assinaturaAtiva, setAssinaturaAtiva] = useState<any>(null);
  const [novoPlanoSelecionado, setNovoPlanoSelecionado] = useState<string | null>(null);
  const [novaFormaPagamento, setNovaFormaPagamento] = useState<'PIX' | 'CREDIT_CARD' | 'BOLETO' | null>(null);
  const [dialogCancelarAssinaturaOpen, setDialogCancelarAssinaturaOpen] = useState(false);
  const [cancelandoAssinatura, setCancelandoAssinatura] = useState(false);
  const [instructionPaymentMethod, setInstructionPaymentMethod] = useState<'PIX' | 'BOLETO' | 'CREDIT_CARD'>('PIX');
  const [pixAutomaticAuthorizationId, setPixAutomaticAuthorizationId] = useState<string | null>(null);
  const [pixAuthorizationInProgress, setPixAuthorizationInProgress] = useState(false);

  // STATUS
  const isPremium = empresa?.is_premium === true;
  const isTrialUser = !isPremium && !!empresa?.trial_end_date;
  const isTrialActive = isTrialUser && !isTrialExpired;
  
  // Detectar se era um assinante que expirou
  const wasSubscriber = () => {
    if (!empresa) return false;
    const trialEndDate = new Date(empresa.trial_end_date || 0);
    const createdDate = new Date(empresa.created_at || 0);
    const trialDuration = 7 * 24 * 60 * 60 * 1000; // 7 dias em ms
    const expectedTrialEnd = new Date(createdDate.getTime() + trialDuration);
    return trialEndDate.getTime() > expectedTrialEnd.getTime();
  };
  
  // ✅ CORRIGIDO: Detecta se era assinante E agora está expirado (isPremium foi false após expiração)
  // Quando assinatura expira, is_premium vira false, mas trial_end_date mostra a data do vencimento
  const isSubscriptionExpired = !isPremium && isTrialExpired && wasSubscriber();
  const isTrialFinished = !isPremium && isTrialExpired && !wasSubscriber();

  // useQuery para buscar dados de pagamento
  const { data: paymentData } = useQuery({
    queryKey: ["subscription", empresa?.id],
    queryFn: async () => {
      if (!empresa?.id) return null;
      const { data, error } = await supabase
        .from("asaas_subscriptions")
        .select("next_due_date, status, asaas_subscription_id, plan_type, billing_type, auto_pix, updated_at")
        .eq("empresa_id", empresa.id)
        .eq("status", "ACTIVE")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error("Erro ao buscar dados de assinatura:", error);
        return null;
      }
      return data;
    },
    enabled: !!empresa?.id && isPremium,
  });

  const expirationDate = paymentData?.next_due_date
    ? new Date(paymentData.next_due_date)
    : null;
  const isPremiumExpired = expirationDate ? new Date() > expirationDate : false;

  // ✅ NOVO: Debug - Monitorar paymentInfo e modal PIX
  useEffect(() => {
    if (showPixInstructions) {
      console.log('🎉🎉🎉 MODAL PIX ABERTO 🎉🎉🎉');
      console.log('📱 paymentInfo completo:', paymentInfo);
      console.log('🔑 pixKey:', paymentInfo?.pixKey);
      console.log('🎯 pixQrCode (base64):', paymentInfo?.pixQrCode ? `${paymentInfo.pixQrCode.substring(0, 50)}...` : 'NÃO ENCONTRADO ❌');
      console.log('📋 instructionPaymentMethod:', instructionPaymentMethod);
      console.log('📊 Condição de renderização PIX:', {
        temPaymentInfo: !!paymentInfo,
        ehPIX: instructionPaymentMethod === 'PIX',
        temQRCode: !!paymentInfo?.pixQrCode,
        temChave: !!paymentInfo?.pixKey,
        deveRenderizar: paymentInfo && instructionPaymentMethod === 'PIX' && (paymentInfo.pixQrCode || paymentInfo.pixKey)
      });
    }
  }, [showPixInstructions, paymentInfo, instructionPaymentMethod]);

  // ✅ NOVO: Verificar assinatura ativa existente
  const checkExistingActiveSubscription = async (): Promise<{
    exists: boolean;
    subscription?: any;
  }> => {
    if (!empresa?.id) return { exists: false };
    
    try {
      console.log('🔍 Verificando assinatura ativa existente...');
      
      // Buscar assinatura ativa
      const { data: activeSubscription, error } = await supabase
        .from('asaas_subscriptions')
        .select('*')
        .eq('empresa_id', empresa.id)
        .eq('status', 'ACTIVE')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('❌ Erro ao buscar assinatura:', error);
        return { exists: false };
      }
      
      if (activeSubscription) {
        // Verificar se data de vencimento é futura
        const nextDue = new Date(activeSubscription.next_due_date);
        const now = new Date();
        
        if (nextDue > now) {
          console.log('✅ Assinatura ativa encontrada:', activeSubscription);
          return { exists: true, subscription: activeSubscription };
        }
      }
      
      return { exists: false };
    } catch (error) {
      console.error('❌ Erro na verificação:', error);
      return { exists: false };
    }
  };

  // HANDLERS
  const handleSubscribe = async (planId: string, isRenewal: boolean = false) => {
    console.log('🚀🚀🚀 HANDLESUBSCRIBE CHAMADO 🚀🚀🚀');
    console.log('📋 planId:', planId);
    console.log('💳 selectedPaymentMethod:', selectedPaymentMethod);
    console.log('🏢 empresa:', empresa);
    
    setIsRenewal(isRenewal);
    setIsLoading(true);

    try {
      // ✅ NOVO: Verificar assinatura ativa PRIMEIRO
      console.log('🔍 Verificando assinatura ativa existente...');
      const { exists: temAssinaturaAtiva, subscription: assinaturaExistente } = 
        await checkExistingActiveSubscription();
      
      if (temAssinaturaAtiva && assinaturaExistente) {
        console.log('⚠️ Usuário já tem assinatura ativa:', assinaturaExistente);
        
        const tipoPlanoAtual = assinaturaExistente.plan_type || 'Desconhecido';
        const dataVencimento = new Date(assinaturaExistente.next_due_date)
          .toLocaleDateString('pt-BR');
        
        toast.warning(
          `Você já possui uma assinatura ${tipoPlanoAtual} ativa até ${dataVencimento}!`
        );
        toast.info('Para trocar de plano, acesse a seção "Trocar Plano" abaixo.');
        
        setIsLoading(false);
        return;
      }

      // Verificar se tem CPF/CNPJ e telefone salvos
      console.log('🔍 Dados da empresa:', empresa);
      console.log('🔍 CPF/CNPJ:', empresa?.cpf_cnpj);
      console.log('🔍 Telefone:', empresa?.telefone);

      if (!empresa?.cpf_cnpj || !empresa?.telefone) {
        console.error('❌ Dados faltando:');
        console.error('❌ CPF/CNPJ:', empresa?.cpf_cnpj);
        console.error('❌ Telefone:', empresa?.telefone);
        toast.error("Complete seus dados de CPF/CNPJ e telefone no cadastro primeiro!");
        setIsLoading(false);
        return;
      }

      // Definir o plano selecionado
      setPendingPlanId(planId);

      // Se for PIX Automático, preparar a autorização primeiro
      let finalPixAuthorization = pixAutomaticAuthorizationId;
      const isAutomaticPix = selectedPaymentMethod === 'PIX_AUTOMATIC';
      
      if (isAutomaticPix && !pixAutomaticAuthorizationId) {
        console.log('✨ Preparando autorização Pix Automático...');
        setPixAuthorizationInProgress(true);
        
        const userName = empresa?.nome || user?.user_metadata?.full_name || 'Usuário';
        const userEmail = user?.email || '';
        
        const authResult = await asaasService.preparePixAutomaticFlow(
          userEmail,
          userName,
          empresa?.id,
          empresa?.cpf_cnpj,
          empresa?.telefone,
          planId
        );

        if (!authResult.success) {
          console.error('❌ Erro ao preparar Pix Automático:', authResult.error);
          toast.error(`Erro ao preparar Pix Automático: ${authResult.error}`);
          setPixAuthorizationInProgress(false);
          return;
        }

        console.log('✨ Autorização Pix Automático preparada:', authResult.data);
        finalPixAuthorization = authResult.data?.pixAutomaticAuthorizationId;
        setPixAutomaticAuthorizationId(finalPixAuthorization);
        
        console.log('🔍 DEBUG - immediateQrCode:', authResult.data?.immediateQrCode);
        console.log('🔍 DEBUG - pix_automatic_authorization_qr_code:', authResult.data?.pix_automatic_authorization_qr_code);
        console.log('🔍 DEBUG - encodedImage:', authResult.data?.encodedImage);
        
        // Mostrar QR Code se disponível
        if (authResult.data?.immediateQrCode) {
          console.log('✅ QR Code encontrado, mostrando modal...');
          setPaymentInfo(authResult.data);
          setPaymentEmail(userEmail);
          setInstructionPaymentMethod('PIX');
          setShowPixInstructions(true);
          
          // Mostrar toast informativo
          toast.success('QR Code de autorização gerado! Escaneie para autorizar.');
          setPixAuthorizationInProgress(false);
          return;
        } else {
          console.log('❌ QR Code NÃO encontrado, continuando...');
        }
        
        setPixAuthorizationInProgress(false);
      }

      // Chamar o serviço ASAAS baseado no planId
      let result;
      const userName = empresa?.nome || user?.user_metadata?.full_name || 'Usuário';
      const userEmail = user?.email || '';

      // ✅ NOVO: Detectar renovação - usar cliente ASAAS existente
      const isPremiumRenewal = isPremium && empresa?.asaas_customer_id;
      const existingCustomerId = isPremiumRenewal ? empresa.asaas_customer_id : undefined;

      if (isPremiumRenewal) {
        console.log('🔄 RENOVAÇÃO DETECTADA - Reutilizando cliente ASAAS:', existingCustomerId);
      }

      switch (planId) {
        case 'monthly-basic':
          result = await asaasService.createMonthlySubscription(
            userEmail,
            userName,
            empresa?.id,
            empresa?.cpf_cnpj,
            empresa?.telefone,
            isAutomaticPix ? 'PIX' : selectedPaymentMethod,
            isAutomaticPix,
            finalPixAuthorization,
            existingCustomerId  // ← NOVO
          );
          break;
        case 'yearly-basic':
          result = await asaasService.createYearlySubscription(
            userEmail,
            userName,
            empresa?.id,
            empresa?.cpf_cnpj,
            empresa?.telefone,
            isAutomaticPix ? 'PIX' : selectedPaymentMethod,
            isAutomaticPix,
            finalPixAuthorization,
            existingCustomerId  // ← NOVO
          );
          break;
        case 'monthly-professional':
          result = await asaasService.createMonthlyProfessionalSubscription(
            userEmail,
            userName,
            empresa?.id,
            empresa?.cpf_cnpj,
            empresa?.telefone,
            isAutomaticPix ? 'PIX' : selectedPaymentMethod,
            isAutomaticPix,
            finalPixAuthorization,
            existingCustomerId  // ← NOVO
          );
          break;
        case 'monthly-professional-plus':
          result = await asaasService.createMonthlyProfessionalPlusSubscription(
            userEmail,
            userName,
            empresa?.id,
            empresa?.cpf_cnpj,
            empresa?.telefone,
            isAutomaticPix ? 'PIX' : selectedPaymentMethod,
            isAutomaticPix,
            finalPixAuthorization,
            existingCustomerId  // ← NOVO
          );
          break;
        case 'monthly-professional-enterprise':
          result = await asaasService.createMonthlyProfessionalEnterpriseSubscription(
            userEmail,
            userName,
            empresa?.id,
            empresa?.cpf_cnpj,
            empresa?.telefone,
            isAutomaticPix ? 'PIX' : selectedPaymentMethod,
            isAutomaticPix,
            finalPixAuthorization,
            existingCustomerId  // ← NOVO
          );
          break;
        case 'yearly-professional':
          result = await asaasService.createYearlyProfessionalSubscription(
            userEmail,
            userName,
            empresa?.id,
            empresa?.cpf_cnpj,
            empresa?.telefone,
            isAutomaticPix ? 'PIX' : selectedPaymentMethod,
            isAutomaticPix,
            finalPixAuthorization
          );
          break;
        // 🆕 CASOS TRIMESTRAIS (adicionados em 03/06/2026)
        case 'quarterly-basic':
          result = await asaasService.createQuarterlyBasicSubscription(
            userEmail,
            userName,
            empresa?.id,
            empresa?.cpf_cnpj,
            empresa?.telefone,
            isAutomaticPix ? 'PIX' : selectedPaymentMethod,
            isAutomaticPix,
            finalPixAuthorization,
            existingCustomerId  // ← NOVO
          );
          break;
        case 'quarterly-professional':
          result = await asaasService.createQuarterlyProfessionalSubscription(
            userEmail,
            userName,
            empresa?.id,
            empresa?.cpf_cnpj,
            empresa?.telefone,
            isAutomaticPix ? 'PIX' : selectedPaymentMethod,
            isAutomaticPix,
            finalPixAuthorization,
            existingCustomerId  // ← NOVO
          );
          break;
        default:
          toast.error('Plano não encontrado');
          return;
      }

      if (result.success) {
        console.log('✅ Assinatura criada com sucesso:', result);
        console.log('📱 result.data:', result.data);
        console.log('🔑 pixKey:', result.data?.pixKey);
        console.log('🎯 pixQrCode existe?:', !!result.data?.pixQrCode);

        // Salvar informações do pagamento para mostrar instruções
        setPaymentInfo(result.data);
        setPaymentEmail(userEmail);

        // Definir o método de instrução baseado no método de pagamento
        const instructionMethod = selectedPaymentMethod === 'PIX_AUTOMATIC' ? 'PIX' : selectedPaymentMethod;
        setInstructionPaymentMethod(instructionMethod as 'PIX' | 'BOLETO' | 'CREDIT_CARD');

        // Abrir modal de instruções
        setShowPixInstructions(true);

        // Recarregar dados da empresa
        await refreshEmpresa(true);

        toast.success('Assinatura criada com sucesso! Verifique seu email.');
      } else {
        console.error('❌ Erro ao criar assinatura:', result.error);
        toast.error(result.error || 'Erro ao processar assinatura. Tente novamente.');
      }
    } catch (error: any) {
      console.error('❌ Erro inesperado:', error);
      toast.error(error?.message || 'Erro inesperado. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTrocarPlano = async () => {
    if (!novoPlanoSelecionado || !assinaturaAtiva?.asaas_subscription_id) {
      toast.error('Selecione um plano para trocar');
      return;
    }

    setTrocandoPlano(true);

    try {
      const temNotaFiscal = novoPlanoSelecionado.includes('professional');
      const result = await asaasService.updateSubscription(
        assinaturaAtiva.asaas_subscription_id,
        novoPlanoSelecionado,
        temNotaFiscal
      );

      if (result.success) {
        toast.success('Plano atualizado com sucesso!');
        setDialogTrocarPlanoOpen(false);
        setNovoPlanoSelecionado(null);
        await refreshEmpresa(true);
      } else {
        toast.error(result.error || 'Erro ao trocar plano');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao trocar plano');
    } finally {
      setTrocandoPlano(false);
    }
  };

  const handleTrocarFormaPagamento = async () => {
    if (!assinaturaAtiva?.asaas_subscription_id || !novaFormaPagamento) {
      toast.error('Selecione uma forma de pagamento');
      return;
    }

    setTrocandoPagamento(true);

    try {
      const result = await asaasService.updatePaymentMethod(
        assinaturaAtiva.asaas_subscription_id,
        novaFormaPagamento
      );

      if (result.success) {
        toast.success('Forma de pagamento atualizada com sucesso!');
        setDialogTrocarPagamentoOpen(false);
        setNovaFormaPagamento(null);
        // Recarregar dados da assinatura
        const updatedResult = await asaasService.getSubscription(assinaturaAtiva.asaas_subscription_id);
        if (updatedResult.success && updatedResult.data) {
          setAssinaturaAtiva({ ...assinaturaAtiva, asaasData: updatedResult.data });
        }
      } else {
        toast.error(result.error || 'Erro ao atualizar forma de pagamento');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao atualizar forma de pagamento');
    } finally {
      setTrocandoPagamento(false);
    }
  };

  const handleCancelarAssinatura = async () => {
    if (!assinaturaAtiva?.asaas_subscription_id) {
      toast.error('Assinatura não encontrada');
      return;
    }

    setCancelandoAssinatura(true);

    try {
      console.log('🔄 Iniciando cancelamento de assinatura...');
      
      // 1. Cancelar no ASAAS
      const asaasResult = await asaasService.cancelSubscription(assinaturaAtiva.asaas_subscription_id);

      if (!asaasResult.success) {
        toast.error(asaasResult.error || "Erro ao cancelar assinatura no ASAAS. Tente novamente.");
        return;
      }

      console.log('✅ Assinatura cancelada no ASAAS');

      // 2. Atualizar banco de dados local
      const { error: updateError } = await supabase
        .from('empresas')
        .update({
          is_premium: false,
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancellation_method: 'user_requested'
        })
        .eq('id', empresa?.id);

      if (updateError) {
        console.warn('⚠️ Erro ao atualizar banco de dados:', updateError);
        // Continuar mesmo se houver erro aqui (não crítico)
      } else {
        console.log('✅ Banco de dados atualizado');
      }

      // 3. Registrar auditoria
      const { error: auditError } = await supabase
        .from('subscription_cancellations')
        .insert({
          empresa_id: empresa?.id,
          asaas_subscription_id: assinaturaAtiva.asaas_subscription_id,
          cancelled_by: 'user',
          cancellation_method: 'user_requested',
          asaas_response: asaasResult.data,
          notes: `Cancelamento realizado via interface do usuário em ${new Date().toLocaleString('pt-BR')}`
        });

      if (auditError) {
        console.warn('⚠️ Erro ao registrar auditoria:', auditError);
      } else {
        console.log('✅ Auditoria registrada');
      }

      // 4. Tentar enviar email de confirmação (não crítico - não falha se erro)
      try {
        const planInfo = getPlanInfo(assinaturaAtiva.plan_type);
        const emailResponse = await fetch('/api/send-cancellation-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: (empresa as any)?.email || user?.email || '',
            empresaNome: empresa?.nome,
            planoAtual: planInfo.name,
            dataVencimento: assinaturaAtiva.asaasData?.nextDueDate || 'Fim do período pago',
            feedbackLink: `${window.location.origin}/feedback-cancelamento?empresa_id=${empresa?.id}`
          })
        });

        if (emailResponse.ok) {
          console.log('✅ Email de confirmação enviado');
        } else {
          console.warn('⚠️ Falha ao enviar email de confirmação');
        }
      } catch (emailError) {
        console.warn('⚠️ Erro ao enviar email:', emailError);
        // Não falha o cancelamento por erro de email
      }

      // 5. Mostrar sucesso e recarregar
      toast.success("✅ Assinatura cancelada com sucesso!\n\nVocê receberá um email de confirmação em breve.");
      setDialogCancelarAssinaturaOpen(false);

      // Recarregar página após 2 segundos
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error: any) {
      console.error("❌ Erro ao cancelar assinatura:", error);
      toast.error(error?.message || "Erro ao cancelar assinatura. Tente novamente ou entre em contato com o suporte.");
    } finally {
      setCancelandoAssinatura(false);
    }
  };

  // RETURNS CONDICIONAIS E JSX DEVEM VIR DEPOIS DOS HOOKS E FUNÇÕES

  // Aguarde os dados essenciais carregarem antes de renderizar qualquer coisa
  if (!empresa || typeof isTrialExpired === 'undefined') {
    return null; // ou um spinner de loading
  }

  // Tela de retenção para trial expirado - REMOVIDA para mostrar planos diretamente
  // if (isTrialFinished) {
  //   return (
  //     <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 p-4">
  //       <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center border border-purple-100">
  //         <h1 className="text-3xl font-bold text-purple-700 mb-2">Seu período de teste expirou 😢</h1>
  //         <p className="text-lg text-gray-700 mb-4">Mas não se preocupe! Você pode continuar usando o Ateliê PRO e desbloquear todos os recursos profissionais.</p>
  //         <div className="mb-6">
  //           <span className="inline-block bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold">Teste encerrado em {empresa?.trial_end_date ? format(new Date(empresa.trial_end_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : "-"}</span>
  //         </div>
  //         <ul className="text-left text-gray-600 mb-6 space-y-2">
  //           <li>✔️ Gestão completa de pedidos</li>
  //           <li>✔️ Calculadora de preços profissional</li>
  //           <li>✔️ Relatórios financeiros</li>
  //           <li>✔️ Suporte prioritário</li>
  //           <li>✔️ E muito mais!</li>
  //         </ul>
  //         <Button
  //           className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg hover:from-purple-700 hover:to-pink-700"
  //           onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
  //         >
  //           Escolher Plano e Assinar
  //         </Button>
  //       </div>
  //     </div>
  //   );
  // }

  // Mapeador de plan_type para dados exibidos na tela
  const getPlanInfo = (planType: string | undefined) => {
    const defaultPlan = {
      name: "Profissional Mensal",
      price: 99.90,
      period: "mês",
      planId: "monthly-professional",
    };

    if (!planType) return defaultPlan;
    
    // ✅ CORRIGIDO: Nomes dos planos como são salvos pelo webhook ASAAS
    const planMap: Record<string, { name: string; price: number; period: string; planId: string }> = {
      // Formatos do webhook (plan_type)
      "monthly-basic": { name: "Básico Mensal", price: 39.00, period: "mês", planId: "monthly-basic" },
      "yearly-basic": { name: "Básico Anual", price: 390.00, period: "ano", planId: "yearly-basic" },
      "quarterly-basic": { name: "Básico Trimestral", price: 79.90, period: "trimestre", planId: "quarterly-basic" },
      "monthly-professional": { name: "Profissional Mensal", price: 99.90, period: "mês", planId: "monthly-professional" },
      "monthly-professional-plus": { name: "Profissional Plus Mensal", price: 189.90, period: "mês", planId: "monthly-professional-plus" },
      "monthly-professional-enterprise": { name: "Profissional Enterprise Mensal", price: 389.90, period: "mês", planId: "monthly-professional-enterprise" },
      "yearly-professional": { name: "Profissional Anual", price: 999.90, period: "ano", planId: "yearly-professional" },
      "quarterly-professional": { name: "Profissional Trimestral", price: 280.00, period: "trimestre", planId: "quarterly-professional" },
      
      // Formatos antigos (se ainda existirem no banco)
      "basic_monthly": { name: "Básico Mensal", price: 39.00, period: "mês", planId: "monthly-basic" },
      "basic_yearly": { name: "Básico Anual", price: 390.00, period: "ano", planId: "yearly-basic" },
      "professional_monthly": { name: "Profissional Mensal", price: 99.90, period: "mês", planId: "monthly-professional" },
      "professional_plus_monthly": { name: "Profissional Plus Mensal", price: 189.90, period: "mês", planId: "monthly-professional-plus" },
      "professional_enterprise_monthly": { name: "Profissional Enterprise Mensal", price: 389.90, period: "mês", planId: "monthly-professional-enterprise" },
      "professional_yearly": { name: "Profissional Anual", price: 999.90, period: "ano", planId: "yearly-professional" },
    };
    
    console.log('🔍 getPlanInfo - Procurando planType:', planType);
    const result = planMap[planType] || defaultPlan;
    console.log('✅ getPlanInfo - Resultado:', result);
    
    return result;
  };

  // Se o usuário já tem premium E a assinatura não está expirada, mostrar página de gerenciamento
  if (isPremium && !isPremiumExpired) {
    const planInfo = getPlanInfo(paymentData?.plan_type);
    return (
      <>
        {/* ✅ Debug: Log da forma de pagamento */}
        {console.log('🔍 Assinatura Ativa - Dados de Pagamento:', {
          billing_type: paymentData?.billing_type,
          plan_type: paymentData?.plan_type,
          next_due_date: paymentData?.next_due_date,
          paymentDataCompleto: paymentData
        })}
        <SubscriptionActiveScreen
        subscriptionData={{
          planName: planInfo.name,
          planPrice: planInfo.price,
          planPeriod: planInfo.period,
          nextBillingDate: paymentData?.next_due_date 
            ? format(new Date(paymentData.next_due_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
            : "Data não definida",
          status: "active",
          paymentMethod: paymentData?.billing_type || "CREDIT_CARD",
          lastPaymentDate: paymentData?.updated_at
            ? format(new Date(paymentData.updated_at), "dd/MM/yyyy", { locale: ptBR })
            : "Data não definida",
          cancelUrl: "https://painel.asaas.com",
        }}
        userName={user?.user_metadata?.full_name?.split(' ')[0] || "usuário"}
        onUpgradePlanClick={() => {
          // Define plano padrão para upgrade (Professional Mensal)
          setPendingPlanId("monthly-professional");
          // Scroll para os planos e abre modal
          window.scrollTo({ top: 0, behavior: "smooth" });
          setIsPaymentModalOpen(true);
        }}
        onChangePaymentClick={() => {
          setDialogTrocarPagamentoOpen(true);
        }}
      />
        
        {/* Modal de Seleção de Forma de Pagamento - Para usuários premium ativos */}
        <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-y-auto">
            <DialogHeader className="text-center pb-4">
              <div className="mx-auto w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-3">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <DialogTitle className="text-xl font-bold text-gray-900">
                Escolha seu Plano
              </DialogTitle>
              <DialogDescription className="text-gray-600 text-sm mt-1">
                Selecione o novo plano e forma de pagamento
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              
              {/* Seção: Seu Plano Atual */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-blue-900 mb-2 uppercase tracking-wide">📍 Seu Plano Atual</p>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-blue-900">{planInfo.name}</h3>
                    <p className="text-sm text-blue-700 mt-1">R$ {planInfo.price.toFixed(2).replace('.', ',')} / {planInfo.period}</p>
                  </div>
                  <Badge className="bg-blue-600 text-white px-3 py-1">Ativo</Badge>
                </div>
              </div>

              {/* Seção: Seletor de Planos */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-semibold text-gray-900">Selecione o Novo Plano</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const el = document.getElementById('hero-plans');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="text-xs h-8"
                  >
                    📊 Comparar Planos
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setPendingPlanId(plan.id)}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        pendingPlanId === plan.id
                          ? plan.featured ? 'border-purple-600 bg-purple-100' : 'border-purple-500 bg-purple-50'
                          : plan.featured ? 'border-purple-300 hover:border-purple-400 bg-purple-25'
                          : 'border-gray-200 hover:border-purple-300 bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 ${
                          pendingPlanId === plan.id
                            ? 'bg-purple-500 border-purple-500'
                            : 'border-gray-300'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <p className="font-semibold text-sm text-gray-900 truncate">{plan.name}</p>
                            {plan.featured && <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded flex items-center gap-1"><span>⭐</span> Melhor custo</span>}
                          </div>
                          <p className="text-xs text-gray-600">R$ {plan.price.toFixed(2).replace('.', ',')}/{plan.period}</p>
                          {plan.discount && (
                            <p className="text-xs text-purple-600 font-semibold mt-1">{plan.discount}</p>
                          )}
                          {plan.monthlyEquivalent && (
                            <p className="text-xs text-purple-700 mt-1">≈ R$ {plan.monthlyEquivalent.toFixed(2).replace('.', ',')}/mês</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Card do Plano Selecionado */}
              {pendingPlanId && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border-2 border-purple-300">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-purple-900">Plano Selecionado</h4>
                    <Badge className="bg-green-100 text-green-800">✓ Pronto</Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-900">
                      {pendingPlanId === 'monthly-basic' ? 'Básico Mensal' :
                       pendingPlanId === 'yearly-basic' ? 'Básico Anual' :
                       pendingPlanId === 'monthly-professional' ? 'Profissional Mensal' :
                       pendingPlanId === 'monthly-professional-plus' ? 'Profissional Plus Mensal' :
                       pendingPlanId === 'monthly-professional-enterprise' ? 'Profissional Enterprise Mensal' :
                       pendingPlanId === 'yearly-professional' ? 'Profissional Anual' :
                       'Plano Selecionado'}
                    </p>
                    <p className="text-sm text-gray-700">
                      Valor: R$ {
                        pendingPlanId === 'monthly-basic' ? '39,00/mês' :
                        pendingPlanId === 'yearly-basic' ? '390,00/ano' :
                        pendingPlanId === 'monthly-professional' ? '99,90/mês' :
                        pendingPlanId === 'monthly-professional-plus' ? '189,90/mês' :
                        pendingPlanId === 'monthly-professional-enterprise' ? '389,90/mês' :
                        pendingPlanId === 'yearly-professional' ? '999,90/ano' :
                        'Valor a confirmar'
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Opções de Pagamento */}
              <div className="space-y-2 pt-2">
                <Label className="text-sm font-semibold text-gray-900">Forma de Pagamento</Label>
                
                {/* PIX Automático */}
                <div
                  onClick={() => setSelectedPaymentMethod('PIX_AUTOMATIC')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'PIX_AUTOMATIC'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedPaymentMethod === 'PIX_AUTOMATIC' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`} />
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-gray-900">PIX Automático</p>
                      <p className="text-xs text-gray-600">Débito automático em sua chave PIX</p>
                    </div>
                    <Badge className="bg-green-100 text-green-800 px-2 py-0.5 text-xs flex-shrink-0">Recomendado</Badge>
                  </div>
                </div>

                {/* PIX Manual */}
                <div
                  onClick={() => setSelectedPaymentMethod('PIX')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'PIX'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedPaymentMethod === 'PIX' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`} />
                    <div>
                      <p className="font-semibold text-sm text-gray-900">PIX Manual</p>
                      <p className="text-xs text-gray-600">QR code que você copia e cola no seu banco</p>
                    </div>
                  </div>
                </div>

                {/* Cartão de Crédito */}
                <div
                  onClick={() => setSelectedPaymentMethod('CREDIT_CARD')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'CREDIT_CARD'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedPaymentMethod === 'CREDIT_CARD' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`} />
                    <div>
                      <p className="font-semibold text-sm text-gray-900">Cartão de Crédito</p>
                      <p className="text-xs text-gray-600">Parcelado em até 12 vezes</p>
                    </div>
                  </div>
                </div>

                {/* Boleto */}
                <div
                  onClick={() => setSelectedPaymentMethod('BOLETO')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'BOLETO'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedPaymentMethod === 'BOLETO' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`} />
                    <div>
                      <p className="font-semibold text-sm text-gray-900">Boleto Bancário</p>
                      <p className="text-xs text-gray-600">Válido por 3 dias</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsPaymentModalOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (pendingPlanId) {
                    handleSubscribe(pendingPlanId);
                    setIsPaymentModalOpen(false);
                  }
                }}
                disabled={!selectedPaymentMethod || isLoading}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processando...
                  </div>
                ) : (
                  'Confirmar Assinatura'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Se o usuário é assinante MAS o pagamento expirou/pendente
  if (isPremium && isPremiumExpired) {
    const planInfo = getPlanInfo(paymentData?.plan_type);
    return (
      <>
        <SubscriptionExpiredScreen
          subscriptionData={{
            planName: planInfo.name,
            planPrice: planInfo.price,
            planPeriod: planInfo.period,
            subscriptionStartDate: paymentData?.created_at
              ? format(new Date(paymentData.created_at), "dd/MM/yyyy", { locale: ptBR })
              : empresa?.created_at
              ? format(new Date(empresa.created_at), "dd/MM/yyyy", { locale: ptBR })
              : "N/A",
            subscriptionEndDate: expirationDate
              ? format(expirationDate, "dd/MM/yyyy", { locale: ptBR })
              : "N/A",
            hoursUntilDeactivation: 48,
          }}
          userName={user?.user_metadata?.full_name?.split(' ')[0] || "usuário"}
          onRenewClick={() => {
            setIsPaymentModalOpen(true);
            setPendingPlanId(planInfo.planId);
          }}
          onUpdatePaymentClick={() => {
            // Abrir diálogo de atualizar forma de pagamento
            setDialogTrocarPagamentoOpen(true);
          }}
        />
        
        {/* Modal de Seleção de Forma de Pagamento - Para usuários com assinatura expirada */}
        <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-y-auto">
            <DialogHeader className="text-center pb-4">
              <div className="mx-auto w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-3">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <DialogTitle className="text-xl font-bold text-gray-900">
                Escolha seu Plano
              </DialogTitle>
              <DialogDescription className="text-gray-600 text-sm mt-1">
                Selecione o plano e forma de pagamento para renovar sua assinatura
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              
              {/* Seção: Seu Plano Anterior */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-orange-900 mb-2 uppercase tracking-wide">⏰ Seu Plano Anterior (Expirado)</p>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-orange-900">{planInfo.name}</h3>
                    <p className="text-sm text-orange-700 mt-1">R$ {planInfo.price.toFixed(2).replace('.', ',')} / {planInfo.period}</p>
                  </div>
                  <Badge className="bg-orange-600 text-white px-3 py-1">Expirado</Badge>
                </div>
              </div>

              {/* Seção: Seletor de Planos */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-semibold text-gray-900">Selecione seu Novo Plano</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const el = document.getElementById('hero-plans');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="text-xs h-8"
                  >
                    📊 Comparar Planos
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setPendingPlanId(plan.id)}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        pendingPlanId === plan.id
                          ? plan.featured ? 'border-purple-600 bg-purple-100' : 'border-purple-500 bg-purple-50'
                          : plan.featured ? 'border-purple-300 hover:border-purple-400 bg-purple-25'
                          : 'border-gray-200 hover:border-purple-300 bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 ${
                          pendingPlanId === plan.id
                            ? 'bg-purple-500 border-purple-500'
                            : 'border-gray-300'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <p className="font-semibold text-sm text-gray-900 truncate">{plan.name}</p>
                            {plan.featured && <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded flex items-center gap-1"><span>⭐</span> Melhor custo</span>}
                          </div>
                          <p className="text-xs text-gray-600">R$ {plan.price.toFixed(2).replace('.', ',')}/{plan.period}</p>
                          {plan.discount && (
                            <p className="text-xs text-purple-600 font-semibold mt-1">{plan.discount}</p>
                          )}
                          {plan.monthlyEquivalent && (
                            <p className="text-xs text-purple-700 mt-1">≈ R$ {plan.monthlyEquivalent.toFixed(2).replace('.', ',')}/mês</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Card do Plano Selecionado */}
              {pendingPlanId && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border-2 border-purple-300">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-purple-900">Plano Selecionado</h4>
                    <Badge className="bg-green-100 text-green-800">✓ Pronto</Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-900">
                      {pendingPlanId === 'monthly-basic' ? 'Básico Mensal' :
                       pendingPlanId === 'yearly-basic' ? 'Básico Anual' :
                       pendingPlanId === 'monthly-professional' ? 'Profissional Mensal' :
                       pendingPlanId === 'monthly-professional-plus' ? 'Profissional Plus Mensal' :
                       pendingPlanId === 'monthly-professional-enterprise' ? 'Profissional Enterprise Mensal' :
                       pendingPlanId === 'yearly-professional' ? 'Profissional Anual' :
                       'Plano Selecionado'}
                    </p>
                    <p className="text-sm text-gray-700">
                      Valor: R$ {
                        pendingPlanId === 'monthly-basic' ? '39,00/mês' :
                        pendingPlanId === 'yearly-basic' ? '390,00/ano' :
                        pendingPlanId === 'monthly-professional' ? '99,90/mês' :
                        pendingPlanId === 'monthly-professional-plus' ? '189,90/mês' :
                        pendingPlanId === 'monthly-professional-enterprise' ? '389,90/mês' :
                        pendingPlanId === 'yearly-professional' ? '999,90/ano' :
                        'Valor a confirmar'
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Opções de Pagamento */}
              <div className="space-y-2 pt-2">
                <Label className="text-sm font-semibold text-gray-900">Forma de Pagamento</Label>
                
                {/* PIX Automático */}
                <div
                  onClick={() => setSelectedPaymentMethod('PIX_AUTOMATIC')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'PIX_AUTOMATIC'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedPaymentMethod === 'PIX_AUTOMATIC' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`} />
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-gray-900">PIX Automático</p>
                      <p className="text-xs text-gray-600">Débito automático em sua chave PIX</p>
                    </div>
                    <Badge className="bg-green-100 text-green-800 px-2 py-0.5 text-xs flex-shrink-0">Recomendado</Badge>
                  </div>
                </div>

                {/* PIX Manual */}
                <div
                  onClick={() => setSelectedPaymentMethod('PIX')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'PIX'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedPaymentMethod === 'PIX' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`} />
                    <div>
                      <p className="font-semibold text-sm text-gray-900">PIX Manual</p>
                      <p className="text-xs text-gray-600">QR code que você copia e cola no seu banco</p>
                    </div>
                  </div>
                </div>

                {/* Cartão de Crédito */}
                <div
                  onClick={() => setSelectedPaymentMethod('CREDIT_CARD')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'CREDIT_CARD'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedPaymentMethod === 'CREDIT_CARD' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`} />
                    <div>
                      <p className="font-semibold text-sm text-gray-900">Cartão de Crédito</p>
                      <p className="text-xs text-gray-600">Parcelado em até 12 vezes</p>
                    </div>
                  </div>
                </div>

                {/* Boleto */}
                <div
                  onClick={() => setSelectedPaymentMethod('BOLETO')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'BOLETO'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedPaymentMethod === 'BOLETO' ? 'bg-purple-500 border-purple-500' : 'border-gray-300'}`} />
                    <div>
                      <p className="font-semibold text-sm text-gray-900">Boleto Bancário</p>
                      <p className="text-xs text-gray-600">Válido por 3 dias</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsPaymentModalOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (pendingPlanId) {
                    handleSubscribe(pendingPlanId);
                    setIsPaymentModalOpen(false);
                  }
                }}
                disabled={!selectedPaymentMethod || isLoading}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processando...
                  </div>
                ) : (
                  'Confirmar Assinatura'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Página original para usuários sem assinatura / trial ativo
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-indigo-200/50 sticky top-0 z-10 shadow-sm">
        <div className="p-4 md:p-6">
          {/* Mobile Layout */}
          <div className="md:hidden">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-indigo-700 hover:bg-indigo-100 p-2"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1">
                <h1 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-purple-600" />
                  Escolha seu Plano
                </h1>
                <p className="text-purple-600 text-xs">Desbloqueie todo o potencial</p>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="text-indigo-700 hover:bg-indigo-100" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-purple-700 hover:bg-purple-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Crown className="w-6 h-6 text-purple-600" />
                  Escolha seu Plano
                </h1>
                <p className="text-purple-600 text-sm mt-0.5">Desbloqueie todo o potencial do Ateliê Pro</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Espaço entre header e trial expirado */}
      <div className="mb-8"></div>

      {/* Assinatura Expirada */}
      {isSubscriptionExpired && (
        <div className="mx-4 md:mx-8 mb-6">
          <Alert className="border-orange-200 bg-orange-50">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <div className="space-y-4">
                <div>
                  <strong className="text-lg">Sua assinatura premium expirou</strong>
                </div>
                <div>
                  Seus dados continuam seguros e armazenados. Para continuar com acesso completo, renove sua assinatura.
                </div>
                <div>
                  Você irá recuperar o acesso imediatamente após o pagamento.
                </div>
                <div className="flex gap-3 mt-4">
                  <Button 
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => {
                      setPendingPlanId('monthly-professional');
                      setIsPaymentModalOpen(true);
                    }}
                    disabled={isLoading}
                  >
                    Renovar Assinatura
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-orange-600 text-orange-600 hover:bg-orange-50"
                    onClick={() => navigate("/")}
                  >
                    Voltar ao Dashboard
                  </Button>
                </div>
                <div className="mt-3 text-center">
                  <button 
                    onClick={() => {
                      const phoneNumber = '5561982321534';
                      const message = 'Olá! Paguei mas meu acesso continua bloqueado. Preciso de ajuda urgente!';
                      const cleanNumber = phoneNumber.replace(/\D/g, '');
                      const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
                      window.open(whatsappUrl, '_blank');
                    }}
                    className="text-sm text-orange-600 hover:text-orange-700 underline hover:no-underline transition-all"
                  >
                    💬 Acesso ainda bloqueado? Fale conosco
                  </button>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Trial Expirado - Versão Mais Profissional */}
      {isTrialFinished && (
        <div className="mx-4 md:mx-8 mb-6">
          <Alert className="border-purple-200 bg-purple-50">
            <AlertTriangle className="h-4 w-4 text-purple-600" />
            <AlertDescription className="text-purple-800">
              <div className="space-y-4">
                <div>
                  <strong className="text-lg">Seu trial do plano Básico expirou</strong>
                </div>
                <div>
                  Durante o teste você pôde organizar pedidos, cadastrar clientes e enxergar melhor o seu ateliê.
                </div>
                <div>
                  Para continuar usando o Ateliê Pro sem interrupções, escolha um dos planos abaixo. Você não perde seus dados.
                </div>
                <div>
                  O teste foi encerrado em 11/02/2026.
                </div>
                <div>
                  Mesmo com o trial encerrado, seus dados continuam salvos por enquanto.
                </div>
                <div className="mt-4">
                  <strong>Recomendado para você</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Plano Básico (R$ 39/mês) – continue com tudo que você já usou no teste.</li>
                    <li>Plano Profissional (R$ 99,90/mês) – adicione emissão de notas fiscais e leve seu ateliê para outro nível.</li>
                  </ul>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button 
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => {
                      setPendingPlanId('monthly-basic');
                      setIsPaymentModalOpen(true);
                    }}
                    disabled={isLoading}
                  >
                    Assinar Plano Básico
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-purple-600 text-purple-600 hover:bg-purple-50"
                    onClick={() => {
                      setPendingPlanId('monthly-professional');
                      setIsPaymentModalOpen(true);
                    }}
                    disabled={isLoading}
                  >
                    Quero emitir NFs (Profissional)
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-indigo-700 hover:bg-indigo-50 border border-transparent ml-2"
                    onClick={() => {
                      const el = document.getElementById('hero-plans');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    Comparar outros planos
                  </Button>
                </div>
                <div className="mt-3 text-center">
                  <button 
                    onClick={() => {
                      const phoneNumber = '5561982321534';
                      const message = 'Olá! Tenho dúvida sobre qual plano escolher para meu ateliê.';
                      const cleanNumber = phoneNumber.replace(/\D/g, '');
                      const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
                      window.open(whatsappUrl, '_blank');
                    }}
                    className="text-sm text-purple-600 hover:text-purple-700 underline hover:no-underline transition-all"
                  >
                    💬 Não sabe qual plano escolher? Fale conosco
                  </button>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Gatilhos de Urgência e Escassez */}
      {isSubscriptionExpired && (
        <div className="mx-4 md:mx-8 mb-6 space-y-4">
          {/* Contador de Urgência - Assinatura Expirada */}
          <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-2">🔒 ACESSO BLOQUEADO</h3>
                <p className="text-orange-100 mb-4">
                  Sua assinatura expirou. Renove agora para recuperar o acesso imediato.
                </p>
              </div>
              <div className="hidden md:block">
                <div className="text-6xl">🔐</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gatilhos de Urgência e Escassez */}
      {isTrialFinished && (
        <div className="mx-4 md:mx-8 mb-6 space-y-4">
          {/* Contador de Urgência */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-2">⚠️ ACESSO LIMITADO</h3>
                <p className="text-purple-100 mb-4">
                  Mantenha seus dados seguros. Escolha um plano para continuar com acesso completo.
                </p>
                <div className="flex items-center gap-4 text-sm mb-4">
                  <div className="bg-white/20 px-3 py-1 rounded-lg">
                    <span className="font-bold">48h</span> restantes
                  </div>
                  <div className="bg-white/20 px-3 py-1 rounded-lg">
                    <span className="font-bold">127</span> usuários ativos hoje
                  </div>
                </div>
                <Button
                  onClick={() => {
                    const phoneNumber = '5561982321534';
                    const message = 'Olá! Já efetuei o pagamento da assinatura, mas meu acesso ainda está bloqueado. Pode me ajudar?';
                    const cleanNumber = phoneNumber.replace(/\D/g, '');
                    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                  className="bg-white text-purple-600 hover:bg-purple-50 font-semibold text-sm"
                >
                  💬 Já efetuei o pagamento
                </Button>
              </div>
              <div className="hidden md:block">
                <div className="text-6xl">⏰</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Social Proof - Mais Profissional */}
      {(isTrialFinished || isSubscriptionExpired) && (
        <div className="mx-4 md:mx-8 mb-8">
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200/50 shadow-sm">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-indigo-800 mb-2">Mais de 500 ateliers já confiam no Ateliê PRO</h3>
                <p className="text-indigo-600">Junte-se aos profissionais que transformaram seus negócios</p>
              </div>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div className="bg-white p-4 rounded-lg border border-indigo-100">
                  <div className="text-3xl font-bold text-blue-600 mb-1">500+</div>
                  <div className="text-sm text-indigo-600">Ateliers ativos</div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-indigo-100">
                  <div className="text-3xl font-bold text-green-600 mb-1">50.000+</div>
                  <div className="text-sm text-indigo-600">Pedidos processados</div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-indigo-100">
                  <div className="text-3xl font-bold text-indigo-600 mb-1">98%</div>
                  <div className="text-sm text-gray-600">Satisfação dos clientes</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="p-4 md:p-8">
        {/* Hero Section - Mais Profissional */}
  <div id="hero-plans" className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Crown className="w-4 h-4" />
            Plataforma Líder para Ateliês
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-indigo-900 mb-6 leading-tight">
            Seu teste acabou. Seu crescimento
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"> não precisa parar.</span>
          </h2>
          <p className="text-xl text-indigo-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Escolha um plano e mantenha seu ateliê funcionando normalmente.
          </p>

          {/* Promotional Alert */}
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-300 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-3 text-center">
              <span className="text-2xl">🎁</span>
              <div>
                <p className="font-bold text-purple-900">Promoção de Lançamento do Plano Trimestral</p>
                <p className="text-sm text-purple-700">Pague 2 meses e use por 3 - Aproveite enquanto válida!</p>
              </div>
              <span className="text-2xl">🚀</span>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center items-center gap-8 mb-8">
            <div className="flex items-center gap-2 text-sm text-indigo-600">
              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
              <span>7 dias grátis</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-indigo-600">
              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
              <span>Sem cartão de crédito</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-indigo-600">
              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
              <span>Cancelamento a qualquer momento</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-indigo-600">
              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
              <span>Suporte brasileiro</span>
            </div>
          </div>
        </div>

        {/* Plans Grid - Design Mais Profissional */}
        <div id="hero-plans" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {plans.map((plan) => {
            const colors = {
              blue: {
                border: "border-indigo-200 hover:border-indigo-300",
                header: "bg-gradient-to-r from-indigo-600 to-purple-600",
                button: "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700",
                accent: "text-indigo-600"
              },
              purple: {
                border: "border-purple-200 hover:border-purple-300",
                header: "bg-gradient-to-r from-purple-600 to-pink-600",
                button: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700",
                accent: "text-purple-600"
              },
              green: {
                border: "border-emerald-200 hover:border-emerald-300",
                header: "bg-gradient-to-r from-emerald-600 to-teal-600",
                button: "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700",
                accent: "text-emerald-600"
              },
              gold: {
                border: "border-amber-200 hover:border-amber-300",
                header: "bg-gradient-to-r from-amber-500 to-orange-500",
                button: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600",
                accent: "text-amber-600"
              }
            };

            const colorScheme = colors[plan.color as keyof typeof colors] || colors.blue;

            return (
              <Card key={plan.id} className={`relative group ${colorScheme.border} ${plan.featured ? 'ring-2 ring-purple-500 shadow-2xl scale-105' : plan.popular ? 'ring-2 ring-purple-300 shadow-xl' : 'shadow-lg hover:shadow-xl'} transition-all duration-300 bg-white overflow-visible border-2`}>
                {/* Promo Label (Promoção de Lançamento) */}
                {plan.promoLabel && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                      🔥 MAIS ESCOLHIDO
                    </div>
                  </div>
                )}

                {/* Popular Badge */}
                {plan.popular && !plan.featured && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                      ⭐ MAIS POPULAR
                    </div>
                  </div>
                )}

                {/* Yearly Badge - Maior Economia */}
                {plan.period === 'ano' && !plan.featured && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
                    <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                      💰 MAIOR ECONOMIA
                    </div>
                  </div>
                )}

                {/* Discount Badge */}
                {plan.discount && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className={`${plan.featured ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'bg-red-500'} text-white px-3 py-1 rounded-md text-xs font-semibold`}>
                      {plan.discount}
                    </div>
                  </div>
                )}

                <CardHeader className={`${colorScheme.header} text-white p-6 ${(plan.featured || plan.popular) && plan.discount ? 'pt-20' : (plan.featured || plan.popular) ? 'pt-10' : ''}`}>
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <plan.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl font-bold">{plan.name}</CardTitle>
                      <div className="flex items-baseline gap-1 mt-2">
                        <span className="text-2xl font-bold">
                          R$ {plan.price.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-sm opacity-90">/{plan.period}</span>
                      </div>
                      {plan.originalPrice && (
                        <div className="text-xs opacity-75 line-through">
                          R$ {plan.originalPrice.toFixed(2).replace('.', ',')}
                        </div>
                      )}
                      {/* Valor Mensal Equivalente - Para Planos Trimestrais */}
                      {plan.monthlyEquivalent && (
                        <div className="text-xs opacity-90 mt-2 italic">
                          Equivale a apenas R$ {plan.monthlyEquivalent.toFixed(2).replace('.', ',')}/mês
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-6">
                  {/* Features List */}
                  <div className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Comparativo - Para Plano Mensal */}
                  {plan.id === 'monthly-basic' && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
                      <div className="space-y-2 text-xs">
                        <p className="text-gray-600">Você paga <span className="font-bold text-indigo-900">R$ {plan.price.toFixed(2).replace('.', ',')}/mês</span></p>
                        <p className="text-gray-500">No trimestral, apenas <span className="font-bold text-indigo-700">R$ 26,63/mês</span></p>
                      </div>
                    </div>
                  )}

                  {/* Savings Highlight - Featured Plans (Trimestral) */}
                  {plan.featured && plan.monthlyEquivalent && (
                    <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-300 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="text-2xl">⭐</div>
                        <div>
                          <div className="text-sm font-semibold text-yellow-900">Melhor custo-benefício</div>
                          <div className="text-xs text-yellow-700">
                            Economize R$ {(plan.originalPrice! - plan.price).toFixed(2).replace('.', ',')} por trimestre
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Savings Highlight for Annual Plans */}
                  {plan.period === 'ano' && !plan.featured && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="text-green-600">💰</div>
                        <div>
                          <div className="text-sm font-semibold text-green-800">Economia anual</div>
                          <div className="text-lg font-bold text-green-700">
                            R$ {((plan.originalPrice || plan.price * 12) - plan.price).toFixed(2).replace('.', ',')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={() => {
                      // Se for o plano mensal básico e o modal ainda não foi descartado, mostrar upgrade
                      if (plan.id === 'monthly-basic' && !upgradeModalDismissed) {
                        setShowUpgradeModal(true);
                      } else {
                        // Comportamento padrão: abrir modal de pagamento
                        setPendingPlanId(plan.id);
                        setIsPaymentModalOpen(true);
                      }
                    }}
                    disabled={isLoading}
                    className={`w-full ${plan.featured ? 'h-14 text-lg' : 'h-12 text-base'} font-semibold text-white shadow-md hover:shadow-xl transition-all duration-200 ${plan.featured ? 'hover:scale-105 hover:-translate-y-1' : 'hover:shadow-lg'} ${colorScheme.button}`}
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processando...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4" />
                        {plan.featured ? 'Garantir Promoção' : 'Escolher Plano'}
                      </div>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Comparação Completa de Planos */}
        <div className="max-w-6xl mx-auto mt-16">
          <Card className="bg-white border border-indigo-200/50 shadow-sm">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-indigo-900">
                Comparação Detalhada de Planos
              </CardTitle>
              <p className="text-indigo-600 mt-2">Escolha o plano ideal para seu negócio</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-indigo-200">
                      <th className="text-left p-4 font-bold text-indigo-900 text-lg">Recursos</th>
                      <th className="text-center p-4 font-bold text-indigo-900">
                        <div className="text-sm">Básico</div>
                        <div className="text-xs text-indigo-600">Mensal/Anual</div>
                      </th>
                      <th className="text-center p-4 font-bold text-indigo-900 bg-indigo-50">
                        <div className="text-sm">Profissional</div>
                        <div className="text-xs text-indigo-600">Mensal</div>
                        <Star className="w-4 h-4 mx-auto mt-1 text-yellow-500" />
                      </th>
                      <th className="text-center p-4 font-bold text-indigo-900">
                        <div className="text-sm">Profissional</div>
                        <div className="text-xs text-indigo-600">Plus</div>
                      </th>
                      <th className="text-center p-4 font-bold text-indigo-900">
                        <div className="text-sm">Profissional</div>
                        <div className="text-xs text-indigo-600">Enterprise</div>
                      </th>
                      <th className="text-center p-4 font-bold text-indigo-900 bg-indigo-50">
                        <div className="text-sm">Profissional</div>
                        <div className="text-xs text-indigo-600">Anual</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Emissão de Notas Fiscais</td>
                      <td className="text-center p-4">✗</td>
                      <td className="text-center p-4 bg-indigo-50">Até 100/mês</td>
                      <td className="text-center p-4">Até 500/mês</td>
                      <td className="text-center p-4">Até 1.000/mês</td>
                      <td className="text-center p-4 bg-indigo-50">Até 100/mês</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Gestão de Clientes</td>
                      <td className="text-center p-4">✓ Básico</td>
                      <td className="text-center p-4 bg-indigo-50">✓ Completo</td>
                      <td className="text-center p-4">✓ Completo</td>
                      <td className="text-center p-4">✓ Completo</td>
                      <td className="text-center p-4 bg-indigo-50">✓ Completo</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Controle de Estoque</td>
                      <td className="text-center p-4">✓ Básico</td>
                      <td className="text-center p-4 bg-indigo-50">✓ Avançado</td>
                      <td className="text-center p-4">✓ Avançado</td>
                      <td className="text-center p-4">✓ Avançado</td>
                      <td className="text-center p-4 bg-indigo-50">✓ Avançado</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Relatórios Financeiros</td>
                      <td className="text-center p-4">✓ Básicos</td>
                      <td className="text-center p-4 bg-indigo-50">✓ Completos</td>
                      <td className="text-center p-4">✓ Completos</td>
                      <td className="text-center p-4">✓ Completos</td>
                      <td className="text-center p-4 bg-indigo-50">✓ Completos</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Suporte</td>
                      <td className="text-center p-4">Email</td>
                      <td className="text-center p-4 bg-indigo-50">Chat + Email</td>
                      <td className="text-center p-4">Chat + Email</td>
                      <td className="text-center p-4">Prioritário</td>
                      <td className="text-center p-4 bg-indigo-50">Chat + Email</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Operadores na Equipe</td>
                      <td className="text-center p-4 font-bold text-red-600">1 (Proprietário)</td>
                      <td className="text-center p-4 bg-indigo-50 font-bold text-green-600">Até 3</td>
                      <td className="text-center p-4 font-bold text-green-600">Até 5 👥</td>
                      <td className="text-center p-4 font-bold text-green-600">Até 5 👥</td>
                      <td className="text-center p-4 bg-indigo-50 font-bold text-green-600">Até 3</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Integrações</td>
                      <td className="text-center p-4">Básicas</td>
                      <td className="text-center p-4 bg-indigo-50">Avançadas</td>
                      <td className="text-center p-4">Avançadas</td>
                      <td className="text-center p-4">Avançadas</td>
                      <td className="text-center p-4 bg-indigo-50">Avançadas</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Backup Automático</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Treinamento Inicial</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Atualizações Gratuitas</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">Certificado SSL</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                    </tr>
                    <tr className="border-b border-indigo-100">
                      <td className="p-4 font-medium text-indigo-900">API Access</td>
                      <td className="text-center p-4">✗</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-indigo-50">✓</td>
                    </tr>
                    <tr className="border-b border-purple-100">
                      <td className="p-4 font-medium text-purple-900">White Label</td>
                      <td className="text-center p-4">✗</td>
                      <td className="text-center p-4 bg-purple-50">✗</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-purple-50">✗</td>
                    </tr>
                    <tr className="border-b border-purple-100">
                      <td className="p-4 font-medium text-purple-900">Setup Personalizado</td>
                      <td className="text-center p-4">✗</td>
                      <td className="text-center p-4 bg-purple-50">✗</td>
                      <td className="text-center p-4">✗</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-purple-50">✗</td>
                    </tr>
                    <tr className="border-b border-purple-100">
                      <td className="p-4 font-medium text-purple-900">Consultoria Dedicada</td>
                      <td className="text-center p-4">✗</td>
                      <td className="text-center p-4 bg-purple-50">✗</td>
                      <td className="text-center p-4">✗</td>
                      <td className="text-center p-4">✓</td>
                      <td className="text-center p-4 bg-purple-50">✗</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        {/* Certificações e Segurança */}
        <div className="max-w-4xl mx-auto mt-12 mb-8">
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200/50">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Segurança e Confiança</h3>
                <p className="text-gray-600">Sua empresa e seus dados estão protegidos pelas melhores práticas do mercado</p>
              </div>
              <div className="grid md:grid-cols-4 gap-6 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm border border-gray-200">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-sm mb-1">SSL 256-bit</h4>
                  <p className="text-xs text-gray-600">Criptografia avançada</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm border border-gray-200">
                    <CheckCircle className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-sm mb-1">LGPD</h4>
                  <p className="text-xs text-gray-600">Conformidade total</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm border border-gray-200">
                    <CheckCircle className="w-8 h-8 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-sm mb-1">Backup Diário</h4>
                  <p className="text-xs text-gray-600">Seus dados seguros</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm border border-gray-200">
                    <CheckCircle className="w-8 h-8 text-orange-600" />
                  </div>
                  <h4 className="font-semibold text-sm mb-1">Suporte 24/7</h4>
                  <p className="text-xs text-gray-600">Sempre disponível</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ - Mais Profissional */}
        <div className="max-w-4xl mx-auto mt-16">
          <Card className="bg-white border border-gray-200/50 shadow-sm">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-gray-900">
                Perguntas Frequentes
              </CardTitle>
              <p className="text-gray-600 mt-2">Tire suas dúvidas sobre nossos planos</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border-b border-gray-100 pb-4">
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Posso cancelar a qualquer momento?</h3>
                <p className="text-gray-600">Sim, você pode cancelar sua assinatura a qualquer momento sem taxas de cancelamento. Seu acesso continua até o final do período pago.</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Como funciona o período de teste?</h3>
                <p className="text-gray-600">Oferecemos 7 dias grátis para você testar todas as funcionalidades. Após esse período, escolha o plano que melhor atende suas necessidades.</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Quais formas de pagamento são aceitas?</h3>
                <p className="text-gray-600">Aceitamos PIX, cartão de crédito (Visa, Mastercard, etc.) e boleto bancário através do ASAAS, nosso parceiro de pagamentos.</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Há suporte técnico?</h3>
                <p className="text-gray-600">Sim, oferecemos suporte por email para todos os usuários. Planos anuais incluem suporte prioritário com resposta em até 4 horas.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Meus dados estão seguros?</h3>
                <p className="text-gray-600">Absolutamente. Utilizamos criptografia de ponta a ponta e seguimos as melhores práticas de segurança. Seus dados nunca são compartilhados com terceiros.</p>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>

      {/* Modal de Upgrade para Trimestral */}
      <UpgradeToQuarterlyModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        onUpgrade={() => {
          setShowUpgradeModal(false);
          setUpgradeModalDismissed(true);
          setPendingPlanId('quarterly-basic');
          setIsPaymentModalOpen(true);
        }}
        onContinueMonthly={() => {
          setShowUpgradeModal(false);
          setUpgradeModalDismissed(true);
          setPendingPlanId('monthly-basic');
          setIsPaymentModalOpen(true);
        }}
      />

      {/* Modal de Seleção de Forma de Pagamento */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="sm:max-w-md max-h-[95vh] overflow-y-auto">
          <DialogHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-3">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold text-gray-900">
              Escolha a Forma de Pagamento
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-sm mt-1">
              Selecione como deseja pagar sua assinatura do Ateliê PRO
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Resumo do Plano */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">
                    {pendingPlanId === 'monthly-basic' ? 'Básico Mensal' :
                     pendingPlanId === 'quarterly-basic' ? 'Básico Trimestral' :
                     pendingPlanId === 'yearly-basic' ? 'Básico Anual' :
                     pendingPlanId === 'monthly-professional' ? 'Profissional Mensal' :
                     pendingPlanId === 'monthly-professional-plus' ? 'Profissional Plus Mensal' :
                     pendingPlanId === 'monthly-professional-enterprise' ? 'Profissional Enterprise Mensal' :
                     pendingPlanId === 'yearly-professional' ? 'Profissional Anual' :
                     'Plano Selecionado'}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {pendingPlanId === 'monthly-basic' ? 'R$ 39,00/mês' :
                     pendingPlanId === 'quarterly-basic' ? 'R$ 79,90/trimestral' :
                     pendingPlanId === 'yearly-basic' ? 'R$ 390,00/ano' :
                     pendingPlanId === 'monthly-professional' ? 'R$ 99,90/mês' :
                     pendingPlanId === 'monthly-professional-plus' ? 'R$ 189,90/mês' :
                     pendingPlanId === 'monthly-professional-enterprise' ? 'R$ 389,90/mês' :
                     pendingPlanId === 'yearly-professional' ? 'R$ 999,90/ano' :
                     'Preço não disponível'}
                  </p>
                </div>
              </div>
            </div>

            {/* Opções de Forma de Pagamento */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">Forma de Pagamento</Label>

              <div className="grid gap-3">
                {/* PIX */}
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'PIX'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                  onClick={() => setSelectedPaymentMethod('PIX')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-green-500 flex items-center justify-center">
                      {selectedPaymentMethod === 'PIX' && (
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">PIX</h4>
                      <p className="text-sm text-gray-600">Pagamento instantâneo</p>
                    </div>
                    <RefreshCw className="w-6 h-6 text-green-500 flex-shrink-0" />
                  </div>
                </div>

                {/* Cartão de Crédito */}
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'CREDIT_CARD'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => setSelectedPaymentMethod('CREDIT_CARD')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-blue-500 flex items-center justify-center">
                      {selectedPaymentMethod === 'CREDIT_CARD' && (
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">Cartão de Crédito</h4>
                      <p className="text-sm text-gray-600">Visa, Mastercard, etc.</p>
                    </div>
                    <CreditCard className="w-6 h-6 text-blue-500 flex-shrink-0" />
                  </div>
                </div>

                {/* PIX Automático */}
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'PIX_AUTOMATIC'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                  onClick={() => setSelectedPaymentMethod('PIX_AUTOMATIC')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-purple-500 flex items-center justify-center">
                      {selectedPaymentMethod === 'PIX_AUTOMATIC' && (
                        <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">PIX Automático</h4>
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-semibold">⭐ RECOMENDADO</span>
                      </div>
                      <p className="text-sm text-gray-600">Renovação automática a cada mês</p>
                    </div>
                    <Zap className="w-6 h-6 text-purple-500 flex-shrink-0" />
                  </div>
                </div>

                {/* Boleto */}
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPaymentMethod === 'BOLETO'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                  onClick={() => setSelectedPaymentMethod('BOLETO')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-orange-500 flex items-center justify-center">
                      {selectedPaymentMethod === 'BOLETO' && (
                        <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">Boleto Bancário</h4>
                      <p className="text-sm text-gray-600">Vencimento em 3 dias úteis</p>
                    </div>
                    <FileText className="w-6 h-6 text-orange-500 flex-shrink-0" />
                  </div>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsPaymentModalOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (pendingPlanId) {
                    handleSubscribe(pendingPlanId);
                    setIsPaymentModalOpen(false);
                  }
                }}
                disabled={!selectedPaymentMethod || isLoading}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processando...
                  </div>
                ) : (
                  'Confirmar Assinatura'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Instruções de Pagamento PIX */}
      <Dialog open={showPixInstructions} onOpenChange={setShowPixInstructions}>
        <DialogContent className="sm:max-w-lg max-h-[95vh] overflow-y-auto">
          <DialogHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-3">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Instruções de Pagamento {instructionPaymentMethod === 'PIX' ? 'PIX' : instructionPaymentMethod === 'BOLETO' ? 'Boleto' : 'Cartão'}
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-sm mt-2">
              Siga os passos abaixo para concluir sua assinatura
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Resumo do Plano */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                {pendingPlanId === 'yearly-basic' || pendingPlanId === 'yearly-professional'
                  ? (pendingPlanId === 'yearly-professional' ? 'Profissional Anual' : 'Básico Anual')
                  : pendingPlanId === 'quarterly-basic' || pendingPlanId === 'quarterly-professional'
                  ? (pendingPlanId === 'quarterly-professional' ? 'Profissional Trimestral' : 'Básico Trimestral')
                  : (pendingPlanId === 'monthly-professional' ? 'Profissional Mensal' : 'Básico Mensal')}
              </h3>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">
                  {pendingPlanId === 'yearly-basic' ? 'R$ 390,00/ano' :
                   pendingPlanId === 'yearly-professional' ? 'R$ 999,90/ano' :
                   pendingPlanId === 'quarterly-basic' ? 'R$ 79,90/trimestre' :
                   pendingPlanId === 'quarterly-professional' ? 'R$ 280,00/trimestre' :
                   pendingPlanId === 'monthly-professional' ? 'R$ 99,90/mês' :
                   'R$ 39,00/mês'}
                </span>
                <span className="text-xl font-bold text-green-600">
                  {pendingPlanId === 'yearly-basic' ? 'R$ 390' :
                   pendingPlanId === 'yearly-professional' ? 'R$ 999,90' :
                   pendingPlanId === 'quarterly-basic' ? 'R$ 79,90' :
                   pendingPlanId === 'quarterly-professional' ? 'R$ 280,00' :
                   pendingPlanId === 'monthly-professional' ? 'R$ 99,90' :
                   'R$ 39'}
                </span>
              </div>
            </div>

            {/* QR Code para PIX Automático - ANTES DOS PASSOS */}
            {paymentInfo && paymentInfo.immediateQrCode && instructionPaymentMethod === 'PIX' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex flex-col gap-3 items-center">
                  <div className="text-3xl">📱</div>
                  <h5 className="font-semibold text-green-900 text-center">QR Code para Autorização PIX</h5>
                  <p className="text-sm text-green-800 text-center">
                    Escaneie com seu celular para autorizar o PIX Automático
                  </p>
                  
                  {/* Exibir QR Code em imagem */}
                  {paymentInfo.pix_automatic_authorization_qr_code && (
                    <div className="mt-4 p-4 bg-white rounded-lg border-2 border-green-300">
                      <img
                        src={`data:image/png;base64,${paymentInfo.pix_automatic_authorization_qr_code}`}
                        alt="QR Code PIX Automático"
                        className="w-56 h-56 object-contain"
                      />
                    </div>
                  )}
                  
                  {/* Informações do QR Code */}
                  {paymentInfo.immediateQrCode && (
                    <div className="text-xs text-green-700 text-center mt-2">
                      <p>Expira em: {new Date(paymentInfo.immediateQrCode.expirationDate).toLocaleString('pt-BR')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* QR Code para PIX Normal (Chave Aleatória) */}
            {paymentInfo && instructionPaymentMethod === 'PIX' && (paymentInfo.pixQrCode || paymentInfo.pixKey) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex flex-col gap-3 items-center">
                  <div className="text-3xl">📱</div>
                  <h5 className="font-semibold text-green-900 text-center">QR Code para Pagamento PIX</h5>
                  <p className="text-sm text-green-800 text-center">
                    Escaneie com seu celular para pagar via PIX
                  </p>
                  
                  {/* Exibir QR Code em imagem */}
                  {paymentInfo.pixQrCode && (
                    <div className="mt-4 p-4 bg-white rounded-lg border-2 border-green-300">
                      <img
                        src={`data:image/png;base64,${paymentInfo.pixQrCode}`}
                        alt="QR Code PIX"
                        className="w-56 h-56 object-contain"
                      />
                    </div>
                  )}

                  {/* Chave PIX para cópia manual */}
                  {paymentInfo.pixKey && (
                    <div className="mt-4 w-full">
                      <p className="text-xs text-green-700 mb-2 text-center">Ou copie a chave PIX:</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={paymentInfo.pixKey}
                          readOnly
                          className="flex-1 px-3 py-2 text-sm border border-green-300 rounded-lg bg-white font-mono"
                        />
                        <Button
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(paymentInfo.pixKey);
                            alert('Chave PIX copiada!');
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          Copiar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Passos */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900 text-lg">Passo a Passo:</h4>

              {/* Passo 1 */}
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-gray-900 mb-1">Verifique seu email</h5>
                  <p className="text-sm text-gray-600">
                    {instructionPaymentMethod === 'PIX'
                      ? 'Você receberá um email com o código PIX para pagamento. O link de pagamento também está disponível acima.'
                      : instructionPaymentMethod === 'BOLETO'
                      ? 'Você receberá um email com o boleto bancário. O link de pagamento também está disponível acima.'
                      : 'Você receberá um email com o link de pagamento. O link também está disponível acima.'}
                  </p>
                </div>
              </div>

              {/* Passo 2 */}
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-gray-900 mb-1">
                    {instructionPaymentMethod === 'PIX' ? 'Pague via PIX' : instructionPaymentMethod === 'BOLETO' ? 'Efetue o pagamento do boleto' : 'Efetue o pagamento'}
                  </h5>
                  <p className="text-sm text-gray-600">
                    {instructionPaymentMethod === 'PIX'
                      ? 'Copie o código PIX e efetue o pagamento usando o app do seu banco.'
                      : instructionPaymentMethod === 'BOLETO'
                      ? 'Baixe o boleto e realize o pagamento no seu banco ou aplicativo.'
                      : 'Acesse o link e complete o pagamento com seu cartão de crédito.'}
                  </p>
                </div>
              </div>

              {/* Passo 3 */}
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-gray-900 mb-1">Aguarde a confirmação</h5>
                  <p className="text-sm text-gray-600">
                    Após o pagamento, sua assinatura será ativada automaticamente em alguns minutos.
                  </p>
                </div>
              </div>

              {/* Passo 4 */}
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">4</span>
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-gray-900 mb-1">Recarregue a página</h5>
                  <p className="text-sm text-gray-600">
                    Quando receber a confirmação, recarregue esta página para atualizar seu status.
                  </p>
                </div>
              </div>
            </div>

            {/* Link de Pagamento (se disponível) */}
            {paymentInfo && (paymentInfo.invoiceUrl || paymentInfo.paymentLink || paymentInfo.bankSlipUrl) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <div className="text-2xl">🔗</div>
                  <div className="flex-1">
                    <h5 className="font-semibold text-blue-900 mb-2">Link de Pagamento Disponível</h5>
                    <p className="text-sm text-blue-800 mb-3">
                      Clique no botão abaixo para acessar o pagamento diretamente:
                    </p>
                    <Button
                      onClick={() => {
                        const url = paymentInfo.invoiceUrl || paymentInfo.paymentLink || paymentInfo.bankSlipUrl;
                        if (url) {
                          window.open(url, '_blank');
                        }
                      }}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Abrir Link de Pagamento
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Informações da Assinatura */}
            {paymentInfo && paymentInfo.id && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <div className="text-2xl">📋</div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">ID da Assinatura</h5>
                    <p className="text-sm text-gray-600 font-mono break-all">
                      {paymentInfo.id}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Guarde este ID para consultas futuras
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Alerta */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex gap-3">
                <div className="text-2xl">⚠️</div>
                <div>
                  <h5 className="font-semibold text-yellow-900 mb-1">Importante</h5>
                  <p className="text-sm text-yellow-800 mb-2">
                    {instructionPaymentMethod === 'PIX'
                      ? 'O código PIX também será enviado por email. Verifique sua caixa de entrada e spam.'
                      : instructionPaymentMethod === 'BOLETO'
                      ? 'O boleto também será enviado por email. Verifique sua caixa de entrada e spam.'
                      : 'O link de pagamento também será enviado por email. Verifique sua caixa de entrada e spam.'}
                  </p>
                  <p className="text-sm text-yellow-800">
                    <strong>Email:</strong> {paymentEmail || paymentInfo?.userEmail || (empresa as any)?.email || user?.email || 'Não informado'}
                  </p>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPixInstructions(false);
                  navigate('/');
                }}
                className="px-6"
              >
                Entendi
              </Button>
              <Button
                onClick={() => {
                  window.location.reload();
                }}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6"
              >
                Recarregar Página
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Button - Sempre visível */}
      <WhatsAppButton
        phoneNumber="5561982321534"
        message="Olá! Tenho uma dúvida sobre minha assinatura no Ateliê PRO. Meu acesso está bloqueado e gostaria de ajuda."
        position="bottom-right"
      />
    </div>
    </div>
  );
}
